import { useEffect, useContext, useState } from "react";
import useAppMain from "src/hooks/useAppMain";
import {
  CLIENT_TAB_LIST,
  ShoppingCartMethods,
  formatter,
} from "src/utils/constants";
import { Button, Flex, Text, Box, useMantineTheme } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { IconGym } from "src/screens/My_Calendar/ScheduleEvent/EventsList/ListItem";
import { useScheduleEventContext } from "src/api/context/ScheduleEventContext";
import { AppContext } from "src/api/AppContext";
import PaymentSelection from "src/components/PaymentSelection";
import {
  UnsavedMethod,
  useCatalogContext,
} from "src/api/context/CatalogContext";
import { openModal } from "@mantine/modals";
import PayrocHostedTransaction from "../../../modules/ProductCatalog/hostedTransaction";
import {
  CreateShoppingCartMutation,
  useCreateShoppingCartMutation,
  useDeleteShoppingCartMutation,
  useUpdateShoppingCartMutation,
} from "src/generated/graphql";

export type Cart = CreateShoppingCartMutation["createShoppingCart"];

function Checkout() {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const cardBg = theme.colors.primary[4];
  useAppMain(CLIENT_TAB_LIST.CHECKOUT, true);
  const { locationService, privateSession, quantity } =
    useScheduleEventContext();

  const { currentClient } = useContext(AppContext);
  const { organizationService } = locationService || {};
  const { session } = privateSession || {};
  const _cost =
    (locationService ? locationService.cost : privateSession?.cost) || 0;
  const _imgUrl = locationService ? locationService.imgUrl : session?.imageUrl;
  const _name = locationService ? organizationService?.name : session?.name;
  const _amount = quantity * (_cost || 0);
  const _product = locationService
    ? locationService.product
    : privateSession?.product;

  const {
    selectedPayment,
    setselectedPayment,
    selectedUnsavedMethod,
    setselectedCustomer,
  } = useCatalogContext();

  useEffect(() => {
    if (currentClient) {
      setselectedCustomer({
        client: currentClient,
      } as any);
    }
  }, [currentClient]);

  const [createShoppingCart] = useCreateShoppingCartMutation();
  const [updateShoppingCart] = useUpdateShoppingCartMutation();
  const [deleteShoppingCart] = useDeleteShoppingCartMutation();

  const [cart, setCart] = useState<Cart | undefined>(undefined);

  const createCart = async () => {
    if (currentClient) {
      const shoppingCart = await createShoppingCart({
        variables: {
          data: {
            client: { id: currentClient.id },
            products: [
              {
                product: { id: _product!.id },
                count: quantity,
              },
            ],
          },
        },
      });
      if (shoppingCart.data?.createShoppingCart?.id) {
        ShoppingCartMethods.set(shoppingCart.data?.createShoppingCart?.id);
        setCart(shoppingCart.data.createShoppingCart);
      }
    }
  };

  const updateCart = async (cartId: string) => {
    if (currentClient) {
      let shoppingCart = await updateShoppingCart({
        variables: {
          id: cartId,
          data: {
            client: { id: currentClient.id },
            products: [
              {
                product: { id: _product!.id },
                count: quantity,
              },
            ],
          },
        },
      });
      if (shoppingCart.data?.updateShoppingCart?.id) {
        setCart(shoppingCart.data.updateShoppingCart);
      }
    }
  };

  useEffect(() => {
    if (cart) {
      updateCart(cart.id);
    }
  }, [_product, quantity]);

  useEffect(() => {
    if (ShoppingCartMethods.get() === null) {
      createCart();
    }
    return () => {
      let _shoppingCartId = ShoppingCartMethods.get();
      if (_shoppingCartId) {
        deleteShoppingCart({
          variables: {
            id: _shoppingCartId,
          },
        });
        ShoppingCartMethods.remove();
      }
    };
  }, []);

  const startTransaction = async () => {
    // Has no discount available
    let tax = 0;
    // isTaxable then calculate Tax Amount
    const { taxRates, isTaxable } = privateSession?.product || {};
    if (isTaxable && taxRates?.length) {
      tax = taxRates.reduce(
        (acc: number, curr) => (acc += curr.taxRate * quantity || 0),
        0 as number
      );
      tax = tax;
    }
    if (cart) {
      openModal({
        modalId: "secureTransactionModal",
        closeOnClickOutside: false,
        title: "Secure Transaction",
        children: (
          <PayrocHostedTransaction
            cart={cart}
            shoppingCartId={ShoppingCartMethods.get() || ""}
            serviceLocId={locationService?.location?.id}
            onComplete={handleNav}
          />
        ),
        styles: {
          modal: {
            backgroundColor: "rgba(0,0,0,0) !important",
          },
          header: {
            backgroundColor: theme.colors.primary[6],
            color: theme.colors.primaryTextColor[6],
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
          },
          body: {
            color:
              selectedUnsavedMethod === UnsavedMethod.Cash
                ? theme.colors.secondaryTextColor[6]
                : "#222222 !important",
            backgroundColor:
              selectedUnsavedMethod === UnsavedMethod.Cash
                ? theme.colors.secondary[6]
                : "#f0f0f0",
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
          },
        },
      });
    }
  };

  useEffect(() => {
    if (!locationService && !privateSession) {
      handleNav();
    }
  }, []);

  const handleNav = () => {
    navigate(-2);
  };

  return (
    <Flex
      direction={"column"}
      justify={"space-between"}
      sx={{ height: "100%" }}
    >
      <Box>
        <Text fw={600} size={18}>
          Items
        </Text>

        <Box
          mt={"xs"}
          bg={cardBg}
          sx={{
            borderRadius: 8,
          }}
        >
          <Box sx={{ padding: "8px 12px" }}>
            <Flex
              justify={"space-between"}
              sx={{ borderBottom: `1px solid #DDDDDD`, paddingBottom: 8 }}
            >
              <Flex align={"center"} gap={"xs"}>
                {IconGym(theme, _imgUrl || "")}
                <Box>
                  <Text size={18}>{_name}</Text>
                  <Text size={12}></Text>
                </Box>
              </Flex>

              <Text size={18}>{formatter.format(_cost)}</Text>
            </Flex>
          </Box>
        </Box>

        <Text fw={600} size={18} mb={"xs"} mt={"md"}>
          Payment
        </Text>
        <Box
          bg={cardBg}
          sx={{
            borderRadius: 8,
          }}
        >
          <Box sx={{ padding: "10px 12px", borderBottom: `1px solid #DDDDDD` }}>
            <Flex justify={"space-between"}>
              <Text size={16} fw={600}>
                Total Amount
              </Text>

              <Text size={16} fw={600}>
                {formatter.format(quantity * _cost)}
              </Text>
            </Flex>
          </Box>
          <Box p={12}>
            <PaymentSelection
              clientId={currentClient?.id}
              selectedPayment={selectedPayment}
              setselectedPayment={setselectedPayment}
              selectedUnsavedMethod={selectedUnsavedMethod}
              getPrimaryPayment={true}
              permittedMethods={[
                UnsavedMethod.Card,
                //UnsavedMethod.Terminal,
                //UnsavedMethod.ACH,
                UnsavedMethod.Cash,
              ]}
            />
          </Box>
        </Box>
      </Box>

      <Box>
        <Text size={10}>
          All sales are final. Refunds are not guaranteed and, if applicable,
          will be resolved on a case-by-case basis. In the case of
          subscriptions, the itemized totals shown above represent only the
          initial charges due at sign-up and are not reflective of the total
          recurring payments due over the term duration.
        </Text>
        <Button
          m={"5px 0"}
          fullWidth
          loading={false}
          disabled={!selectedPayment && !selectedUnsavedMethod}
          onClick={startTransaction}
        >
          Buy Now {formatter.format(_amount)}
        </Button>
      </Box>
    </Flex>
  );
}

export default Checkout;
